import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import {
  getProjectsPageByUserId,
  mapProjectToCard,
} from "@/services/projects/projectService";
import { PROJECT_DELETED_EVENT } from "@/utils/dataSyncEvents";
import { DASHBOARD_RECENT_PROJECTS } from "@/utils/paginationConstants";

/**
 * Projetos recentes do dashboard: query Firestore com limit, sem carregar a coleção inteira.
 *
 * @param {number} [limitCount]
 */
export function useRecentProjects(limitCount = DASHBOARD_RECENT_PROJECTS) {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleProjectDeleted = (event) => {
      const { projectId } = event.detail ?? {};

      if (!projectId) {
        return;
      }

      setProjects((current) =>
        current.filter((project) => project.id !== projectId),
      );
    };

    window.addEventListener(PROJECT_DELETED_EVENT, handleProjectDeleted);

    return () => {
      window.removeEventListener(PROJECT_DELETED_EVENT, handleProjectDeleted);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!user?.uid) {
        if (!cancelled) {
          setProjects([]);
          setLoading(false);
        }
        return;
      }

      if (!cancelled) {
        setLoading(true);
        setError(null);
      }

      try {
        const result = await getProjectsPageByUserId(user.uid, {
          limitCount,
        });

        if (!cancelled) {
          setProjects(result.items);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err);
          setProjects([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [user?.uid, limitCount]);

  const cardProjects = useMemo(
    () => projects.map(mapProjectToCard),
    [projects],
  );

  return { projects, cardProjects, loading, error };
}
